import Stepping from 'sections/CustomerAccounts/Withdrawal/Stepper';
import { Page } from '../theme/elements/Page';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { Box, Typography, useTheme } from '@mui/material';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCompanyDashboard } from 'state/reducers/customer-accounts.reducers';
import { getCustomerDashboard } from 'services/customer-accounts.service';
import _ from 'lodash';
import { withdrawalBalanceSteps } from 'constants/customer-account.constants';
import { withdrawalBalanceTypesForms } from 'sections/CustomerAccounts/forms.types';
import { pagesEnum } from 'constants/ui.constants';
import { WithdrawBalanceStep1 } from 'sections/CustomerAccounts/Withdrawal/steps/step1';
import { WithdrawBalanceStep2 } from 'sections/CustomerAccounts/Withdrawal/steps/step2';
import { getSupportRequestById as getSupportRequestByIdService } from 'services/pending-requests.service';
import { SupportStatus } from 'constants/pending-requests.constants';
import { BalanceStatus } from 'constants/balance.constants';
import { WithdrawBalanceStep3 } from 'sections/CustomerAccounts/Withdrawal/steps/step3';
import { getUserDetails } from 'state/reducers/user.reducers';

export const WithdrawalApp = () => {
	const t = useAppSelector(selectTranslations);
	const { id, requestId } = useParams();
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [supportRequest, setSupportRequest] = useState<Record<string, any>>({});
	const [balanceRequest, setBalanceRequest] = useState<Record<string, any>>({});
	const companyData = useAppSelector(getCompanyDashboard);

	const withdrawalBalanceForms = useMemo(() => {
		return companyData ? withdrawalBalanceTypesForms() : null;
	}, [companyData]);

	const currentStep: withdrawalBalanceSteps | null = useMemo(() => {
		if (withdrawalBalanceForms && (supportRequest || requestId === 'new')) {
			if (
				(requestId === 'new' && !_.keys(supportRequest).length) ||
				_.isEqual(supportRequest.status, SupportStatus.Pending)
			) {
				return withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST;
			} else if (
				_.isEqual(supportRequest.status, SupportStatus.Completed) &&
				!_.isNil(supportRequest.activityBalanceAddition.ccId) &&
				_.isEqual(supportRequest.activityBalanceAddition.status, BalanceStatus.PendingCCTransfer)
			) {
				return withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_CC_TO_BANK_ACCOUNT;
			} else if (
				_.isEqual(supportRequest.status, SupportStatus.Completed) &&
				_.isEqual(supportRequest.activityBalanceAddition.status, BalanceStatus.Pending) &&
				(_.isEqual(supportRequest.activityBalanceAddition.isBoSignersRequired, false) ||
					(_.isEqual(supportRequest.activityBalanceAddition.isBoSignersRequired, true) &&
						//supportRequest.activityBalanceAddition.boApprovedByMainSignerUserId &&
						supportRequest.activityBalanceAddition.boApprovedBySecondarySignerUserId))
			) {
				return withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT;
			}
		}
		return null;
	}, [supportRequest, requestId, withdrawalBalanceForms]);

	const currentStepIdx = useMemo(() => {
		if (currentStep) return Object.values(withdrawalBalanceSteps).findIndex((val) => val === currentStep) + 1;
		return 0;
	}, [currentStep]);

	const dispatchGetSupportRequest = () => {
		dispatch(getCustomerDashboard(+id!));
	};
	useEffect(() => {
		if (id) {
			dispatch(getCustomerDashboard(+id));
		} else {
			navigate(`/${pagesEnum.COMPANY_FEATURE_PROFILE}/`);
		}
	}, [id, dispatch]);

	useEffect(() => {
		if (requestId && requestId !== 'new' && _.isInteger(+requestId) && +requestId > 0) {
			dispatch(getSupportRequestByIdService(+requestId))
				.unwrap()
				.then((originalPromiseResult) => {
					setSupportRequest(originalPromiseResult.supportRequest);
				});
		}
	}, [requestId, dispatch]);

	useEffect(() => {
		if (supportRequest && supportRequest.activityBalanceAddition) {
			if (
				_.isEqual(supportRequest.activityBalanceAddition.status, BalanceStatus.PendingBoSignerReview) &&
				(_.isNil(supportRequest.activityBalanceAddition.boApprovedByMainSignerUserId) ||
					_.isNil(supportRequest.activityBalanceAddition.boApprovedBySecondarySignerUserId))
			) {
				navigate(-1);
			}
		}
	}, [supportRequest]);

	const handleNavigateToCompanyPage = () => {
		navigate(`/${pagesEnum.CUSTOMER_ACCOUNTS}/${id}`);
	};
	return (
		<Page title={t('page.withdrawal.title')}>
			<Fragment>
				{currentStepIdx > 0 && (
					<>
						<Box marginTop="24px" width={theme.sizes.STEPPER_WIDTH}>
							<Stepping prefix="page.withdrawal.stepper.step." steps={3} activeStep={currentStepIdx} />
						</Box>

						<Typography sx={{ marginTop: '16px' }} variant="h5">
							{t(`page.withdrawal.step.${currentStepIdx}.subtitle`)}
						</Typography>
					</>
				)}
				{withdrawalBalanceForms && currentStep === withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST && (
					<WithdrawBalanceStep1
						companyId={+id!}
						dispatchGetSupportRequest={dispatchGetSupportRequest}
						supportRequest={supportRequest}
						withdrawalBalanceForm={
							withdrawalBalanceForms![withdrawalBalanceSteps.WITHDRAWAL_INITIAL_REQUEST]
						}
					/>
				)}
				{withdrawalBalanceForms &&
					currentStep === withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_CC_TO_BANK_ACCOUNT && (
						<WithdrawBalanceStep2
							dispatchGetSupportRequest={dispatchGetSupportRequest}
							balanceRequest={supportRequest.activityBalanceAddition}
							withdrawalBalanceForm={
								withdrawalBalanceForms![withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_CC_TO_BANK_ACCOUNT]
							}
						/>
					)}
				{withdrawalBalanceForms &&
					currentStep === withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT && (
						<WithdrawBalanceStep3
							handleNavigateToCompanyPage={handleNavigateToCompanyPage}
							balanceRequest={supportRequest.activityBalanceAddition}
							withdrawalBalanceForm={
								withdrawalBalanceForms![
									withdrawalBalanceSteps.WITHDRAWAL_ALTSHULER_TO_CUSTOMER_BANK_ACCOUNT
								]
							}
							supportRequest={supportRequest}
						/>
					)}

				{/* {activeStep === 1 && } */}
			</Fragment>
		</Page>
	);
};
